import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { geoNaturalEarth1, geoPath, geoGraticule10 } from "d3-geo";
import countries from "../data/countries";
import ISO from "../data/iso";
import COUNTRY_COLORS from "../data/colors";
import { CONT_VIEWS } from "../data/constants";
import topoFeature from "../utils/topoFeature";
import Tooltip from "./Tooltip";

const TOPO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const W = 960, H = 540;

// Countries too small for the 110m TopoJSON — rendered as custom markers
const MICRO_STATE_CODES = new Set(["AD", "BH", "BN", "LI", "MT", "MC", "MV", "SM", "SG", "TL", "VA"]);
// Countries that are also micro-states but always need a visible red summit pin
const ALWAYS_PIN_CODES = new Set(["MV"]);

const ELEV_BANDS = [
  { min: 8000, color: "#8B1818", label: "8000+" },
  { min: 7000, color: "#C43232", label: "7000–8000" },
  { min: 6000, color: "#E07830", label: "6000–7000" },
  { min: 5000, color: "#C8A840", label: "5000–6000" },
  { min: 4000, color: "#90B840", label: "4000–5000" },
  { min: 3000, color: "#50A050", label: "3000–4000" },
  { min: 2500, color: "#3A9060", label: "2500–3000" },
  { min: 2000, color: "#30A888", label: "2000–2500" },
  { min: 1500, color: "#28B0A8", label: "1500–2000" },
  { min: 1000, color: "#4090CC", label: "1000–1500" },
  { min: 500,  color: "#4060B8", label: "500–1000" },
  { min: 0,    color: "#6040A8", label: "0–500" },
];

function getElevationColor(elevation) {
  if (elevation == null) return "#D1D5DB";
  for (const band of ELEV_BANDS) {
    if (elevation >= band.min) return band.color;
  }
  return "#D1D5DB";
}

export default function WorldMap({ onSelect, cont, search, showAllPins, choropleth, category }) {
  const svgRef = useRef(null);
  const [geo, setGeo] = useState(null);
  const [tip, setTip] = useState({ v: false, x: 0, y: 0, c: null });
  const [hoveredCode, setHoveredCode] = useState(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
  const dragRef = useRef(null);
  const transformRef = useRef(transform);
  useEffect(() => { transformRef.current = transform; }, [transform]);

  const cMap = useMemo(() => {
    const m = {};
    countries.forEach((c) => (m[c.code] = c));
    return m;
  }, []);

  const matchSet = useMemo(() => {
    const s = new Set();
    const q = (search || "").toLowerCase();
    countries.forEach((c) => {
      const mc = cont === "All" || c.continent === cont;
      const ms = !q || c.name.toLowerCase().includes(q) || c.peak.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
      const mcat = !category || category === "All" || c.category === category.toLowerCase();
      if (mc && ms && mcat) s.add(c.code);
    });
    return s;
  }, [cont, search, category]);

  const filt = cont !== "All" || (search || "") !== "" || (category && category !== "All");

  useEffect(() => {
    fetch(TOPO_URL)
      .then((r) => r.json())
      .then((topo) => setGeo(topoFeature(topo, topo.objects.countries)))
      .catch(() => setGeo("error"));
  }, []);

  useEffect(() => {
    const v = CONT_VIEWS[cont] || CONT_VIEWS["All"];
    setTransform({ x: 480 - v.x * v.k, y: 270 - v.y * v.k, k: v.k });
  }, [cont]);


  const proj = useMemo(() => geoNaturalEarth1().scale(210).translate([W / 2, H / 2 - 20]), []);
  const pathGen = useMemo(() => geoPath(proj), [proj]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * W;
    const my = ((e.clientY - rect.top) / rect.height) * H;
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setTransform((t) => {
      const nk = Math.max(0.8, Math.min(12, t.k * factor));
      return { x: mx - (mx - t.x) * (nk / t.k), y: my - (my - t.y) * (nk / t.k), k: nk };
    });
  }, []);

  const handleDown = useCallback(
    (e) => {
      if (e.button !== 0) return;
      const svg = svgRef.current;
      if (!svg) return;
      dragRef.current = { sx: e.clientX, sy: e.clientY, tx: transform.x, ty: transform.y };
      const onMove = (ev) => {
        if (!dragRef.current) return;
        const rect = svg.getBoundingClientRect();
        const s = W / rect.width;
        const dx = (ev.clientX - dragRef.current.sx) * s;
        const dy = (ev.clientY - dragRef.current.sy) * s;
        setTransform((t) => ({ ...t, x: dragRef.current.tx + dx, y: dragRef.current.ty + dy }));
      };
      const onUp = () => {
        dragRef.current = null;
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [transform.x, transform.y]
  );

  // Touch handling (pinch-to-zoom + drag)
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    let t1Start = null, t2Start = null, txStart = null, hasMoved = false;
    const MOVE_THRESH = 5;

    const onTouchStart = (e) => {
      txStart = { ...transformRef.current };
      hasMoved = false;
      t1Start = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      t2Start = e.touches.length >= 2 ? { x: e.touches[1].clientX, y: e.touches[1].clientY } : null;
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      if (!txStart) return;
      const rect = svg.getBoundingClientRect();
      const s = W / rect.width;

      if (e.touches.length === 1 && t1Start && !t2Start) {
        const t = e.touches[0];
        const dx = (t.clientX - t1Start.x) * s;
        const dy = (t.clientY - t1Start.y) * s;
        if (Math.hypot(t.clientX - t1Start.x, t.clientY - t1Start.y) > MOVE_THRESH) hasMoved = true;
        setTransform({ ...txStart, x: txStart.x + dx, y: txStart.y + dy });
      } else if (e.touches.length >= 2 && t1Start && t2Start) {
        hasMoved = true;
        const a = e.touches[0], b = e.touches[1];
        const curDist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        const startDist = Math.hypot(t2Start.x - t1Start.x, t2Start.y - t1Start.y);
        if (startDist === 0) return;

        const nk = Math.max(0.8, Math.min(12, txStart.k * (curDist / startDist)));
        const curMidX = ((a.clientX + b.clientX) / 2 - rect.left) / rect.width * W;
        const curMidY = ((a.clientY + b.clientY) / 2 - rect.top) / rect.height * H;
        const startMidX = ((t1Start.x + t2Start.x) / 2 - rect.left) / rect.width * W;
        const startMidY = ((t1Start.y + t2Start.y) / 2 - rect.top) / rect.height * H;

        setTransform({
          x: curMidX - (startMidX - txStart.x) * (nk / txStart.k),
          y: curMidY - (startMidY - txStart.y) * (nk / txStart.k),
          k: nk,
        });
      }
    };

    const onTouchEnd = (e) => {
      if (hasMoved) e.preventDefault(); // prevent accidental click after drag
    };

    svg.addEventListener('touchstart', onTouchStart, { passive: true });
    svg.addEventListener('touchmove', onTouchMove, { passive: false });
    svg.addEventListener('touchend', onTouchEnd, { passive: false });
    return () => {
      svg.removeEventListener('touchstart', onTouchStart);
      svg.removeEventListener('touchmove', onTouchMove);
      svg.removeEventListener('touchend', onTouchEnd);
    };
  }, [geo]); // re-run once map SVG is rendered

  const zoomIn = () => setTransform((t) => { const nk = Math.min(12, t.k * 1.4); return { x: 480 - (480 - t.x) * (nk / t.k), y: 270 - (270 - t.y) * (nk / t.k), k: nk }; });
  const zoomOut = () => setTransform((t) => { const nk = Math.max(0.8, t.k / 1.4); return { x: 480 - (480 - t.x) * (nk / t.k), y: 270 - (270 - t.y) * (nk / t.k), k: nk }; });
  const resetZoom = () => { const v = CONT_VIEWS[cont] || CONT_VIEWS["All"]; setTransform({ x: 480 - v.x * v.k, y: 270 - v.y * v.k, k: v.k }); };

  const enter = useCallback((e, code) => {
    const c = cMap[code];
    if (c) {
      setTip({ v: true, x: e.clientX, y: e.clientY - 12, c });
      setHoveredCode(code);
    }
  }, [cMap]);

  const move = useCallback((e) => {
    setTip((t) => (t.v ? { ...t, x: e.clientX, y: e.clientY - 12 } : t));
  }, []);

  const leave = useCallback(() => {
    setTip((t) => ({ ...t, v: false }));
    setHoveredCode(null);
  }, []);

  if (geo === "error") return <p style={{ color: "var(--t2)", padding: 20 }}>Could not load map data. Please refresh.</p>;
  if (!geo) return <div style={{ padding: 60, textAlign: "center", color: "var(--t3)" }}>Loading world map...</div>;

  const tf = `translate(${transform.x},${transform.y}) scale(${transform.k})`;

  // Which peak dots to render:
  // showAllPins ON  → all dots (respecting continent/search filter)
  // showAllPins OFF → only the currently hovered country's dot
  // Micro-states are excluded here — they get their own markers below
  const visiblePins = countries.filter((c) => {
    if (!c.peakLat || !c.peakLng) return false;
    if (MICRO_STATE_CODES.has(c.code) && !ALWAYS_PIN_CODES.has(c.code)) return false;
    if (filt && !matchSet.has(c.code)) return false;
    if (ALWAYS_PIN_CODES.has(c.code)) return true;
    if (showAllPins) return true;
    return c.code === hoveredCode;
  });

  // Micro-state markers: only visible when zoomed into a continent
  const microStateMarkers = cont === "All" ? [] : countries.filter((c) => {
    if (!MICRO_STATE_CODES.has(c.code)) return false;
    if (!c.peakLat || !c.peakLng) return false;
    if (!matchSet.has(c.code)) return false;
    return true;
  });

  return (
    <>
      <div className="mw">
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={{ width: "100%", height: "100%" }} onWheel={handleWheel} onMouseDown={handleDown}>
          <g style={{ transition: dragRef.current ? "none" : "transform .5s ease" }}>
            <g transform={tf}>
              <path d={pathGen(geoGraticule10())} fill="none" stroke="#eaeced" strokeWidth={0.25 / transform.k} />
              {geo.features.map((f, i) => {
                const code = ISO[f.id] || ISO[String(f.id)] || (f.properties?.name === "Kosovo" ? "XK" : null);
                const c = code ? cMap[code] : null;
                const color = choropleth
                  ? (c ? getElevationColor(c.elevation) : "#D1D5DB")
                  : (code && COUNTRY_COLORS[code] ? COUNTRY_COLORS[code] : "#D1D5DB");
                const isMatch = code ? matchSet.has(code) : false;
                const d = pathGen(f);
                if (!d) return null;
                return (
                  <path
                    key={i} d={d} fill={color}
                    style={{ strokeWidth: Math.max(0.2, 0.4 / transform.k) }}
                    className={filt ? (isMatch ? "hl" : "dim") : ""}
                    onMouseEnter={(e) => enter(e, code)}
                    onMouseMove={move}
                    onMouseLeave={leave}
                    onClick={() => { if (c) onSelect(code); }}
                  />
                );
              })}
              {microStateMarkers.map((c) => {
                const pt = proj([c.peakLng, c.peakLat]);
                if (!pt) return null;
                const r = Math.max(1.5, 2.5 / transform.k);
                const color = choropleth ? getElevationColor(c.elevation) : (COUNTRY_COLORS[c.code] || "#888");
                const isMatch = matchSet.has(c.code);
                return (
                  <g key={"ms-" + c.code}
                    className={filt ? (isMatch ? "hl" : "dim") : ""}
                    onMouseEnter={(e) => enter(e, c.code)}
                    onMouseMove={move}
                    onMouseLeave={leave}
                    onClick={() => onSelect(c.code)}
                    style={{ cursor: "pointer" }}
                  >
                    <circle cx={pt[0]} cy={pt[1]} r={r + 1.5} fill="rgba(0,0,0,.15)" />
                    <circle cx={pt[0]} cy={pt[1]} r={r} fill={color} stroke="#fff" strokeWidth={Math.max(0.4, 1 / transform.k)} />
                  </g>
                );
              })}
              {visiblePins.map((c) => {
                const pt = proj([c.peakLng, c.peakLat]);
                if (!pt) return null;
                const r = Math.max(1.5, 3 / transform.k);
                return (
                  <g key={"pin-" + c.code}
                    onMouseEnter={(e) => enter(e, c.code)}
                    onMouseMove={move}
                    onMouseLeave={leave}
                    onClick={() => onSelect(c.code)}
                    style={{ cursor: "pointer" }}
                  >
                    <circle cx={pt[0]} cy={pt[1]} r={r + 1} fill="rgba(0,0,0,.15)" />
                    <circle cx={pt[0]} cy={pt[1]} r={r} fill="#DC2626" stroke="#fff" strokeWidth={Math.max(0.3, 0.8 / transform.k)} />
                  </g>
                );
              })}
            </g>
          </g>
        </svg>
        <div className="zoom-controls">
          <button className="zoom-btn" onClick={zoomIn} title="Zoom in">+</button>
          <button className="zoom-btn" onClick={zoomOut} title="Zoom out">−</button>
          <button className="zoom-btn" onClick={resetZoom} title="Reset" style={{ fontSize: 13 }}>↻</button>
        </div>
        <div className="zoom-hint">
          <span className="hint-mouse">Scroll to zoom · Drag to pan</span>
          <span className="hint-touch">Pinch to zoom · Drag to pan</span>
        </div>
        {choropleth && (
          <div className="choro-legend">
            <div className="choro-legend-title">Highest Point (m)</div>
            {ELEV_BANDS.map((b) => (
              <div key={b.min} className="choro-legend-row">
                <span className="choro-legend-swatch" style={{ background: b.color }} />
                <span className="choro-legend-label">{b.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <Tooltip visible={tip.v} x={tip.x} y={tip.y} country={tip.c} />
    </>
  );
}
